// ./src/components/features/Travels/TravelWaypointsDrawer/MobileMapOverlay.tsx
import React, { useState, useCallback } from "react";
import {
  Box,
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  ActionIcon,
  Transition,
  Overlay,
  CloseButton,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { EXPENSE_TYPES } from "@/config";
import { Expense } from "@/utils/api/ExpenseService";

import { AppIcon } from "@/components/generic/AppIcon";

import { PlaceTypeBadge } from "@/components/features/SavedPlaces/PlaceTypeBadge";

import { WaypointsMapView } from "./WaypointsMapView";

// ============================================
// Types
// ============================================

export interface MobileMapOverlayProps {
  /** Whether the overlay is visible */
  opened: boolean;

  /** Function to close the overlay */
  onClose: () => void;

  /** All expenses/waypoints to display on the map */
  expenses: Expense[];

  /** Waypoints only (for determining first/last) */
  waypoints: Expense[];

  /** Distance unit label */
  distanceLabel: string;

  /** Volume unit label */
  volumeLabel: string;

  /** Currency symbol */
  currencySymbol: string;

  encodedPolyline?: string | null;

  /** Callback when edit is requested */
  onEditWaypoint?: (waypoint: Expense) => void;
}

// ============================================
// Helper Functions
// ============================================

const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return "—";
  return dayjs(dateString).format("MMM DD, YYYY HH:mm");
};

// ============================================
// Waypoint Detail Card Component
// ============================================

interface WaypointDetailCardProps {
  waypoint: Expense;
  waypoints: Expense[];
  distanceLabel: string;
  volumeLabel: string;
  currencySymbol: string;
  onClose: () => void;
  onEdit?: (waypoint: Expense) => void;
}

const WaypointDetailCard: React.FC<WaypointDetailCardProps> = ({
  waypoint,
  waypoints,
  distanceLabel,
  volumeLabel,
  currencySymbol,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation(["travels", "expenses", "refuels"]);

  // Determine position for travel points
  const waypointIndex = waypoints.findIndex((w) => w.id === waypoint.id);
  const isFirst = waypointIndex === 0;
  const isLast = waypointIndex === waypoints.length - 1;
  const isWaypoint = waypoint.expenseType === EXPENSE_TYPES.TRAVEL_POINT;

  const getPositionLabel = (): string => {
    if (!isWaypoint) return "";
    if (isFirst) return t("travels:waypoints.labels.start");
    if (isLast) return t("travels:waypoints.labels.end");
    return t("travels:waypoints.labels.stop", { number: waypointIndex });
  };

  const getTypeIcon = (): string => {
    switch (waypoint.expenseType) {
      case EXPENSE_TYPES.TRAVEL_POINT:
        return "MapPin";
      case EXPENSE_TYPES.REFUEL:
        return "GasStation";
      case EXPENSE_TYPES.EXPENSE:
        return "Receipt";
      case EXPENSE_TYPES.CHECKPOINT:
        return "Flag";
      case EXPENSE_TYPES.REVENUE:
        return "CurrencyDollar";
      default:
        return "MapPin";
    }
  };

  const getTypeColor = (): string => {
    if (isWaypoint) {
      if (isFirst) return "green";
      if (isLast) return "red";
      return "blue";
    }
    switch (waypoint.expenseType) {
      case EXPENSE_TYPES.REFUEL:
        return "orange";
      case EXPENSE_TYPES.EXPENSE:
        return "red";
      case EXPENSE_TYPES.CHECKPOINT:
        return "gray";
      case EXPENSE_TYPES.REVENUE:
        return "green";
      default:
        return "blue";
    }
  };

  const getTypeLabel = (): string => {
    switch (waypoint.expenseType) {
      case EXPENSE_TYPES.TRAVEL_POINT:
        return getPositionLabel();
      case EXPENSE_TYPES.REFUEL:
        return t("travels:waypoints.sections.refuels");
      case EXPENSE_TYPES.EXPENSE:
        return t("travels:waypoints.sections.expenses");
      case EXPENSE_TYPES.CHECKPOINT:
        return t("travels:waypoints.sections.checkpoints");
      case EXPENSE_TYPES.REVENUE:
        return t("travels:waypoints.sections.revenues");
      default:
        return "";
    }
  };

  const color = getTypeColor();

  return (
    <Paper
      p="md"
      radius="lg"
      shadow="xl"
      style={{
        borderTop: `4px solid var(--mantine-color-${color}-5)`,
      }}
    >
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <Box
              p={8}
              style={{
                backgroundColor: `var(--mantine-color-${color}-0)`,
                borderRadius: "var(--mantine-radius-md)",
              }}
            >
              <AppIcon
                iconName={getTypeIcon()}
                size={20}
                color={`var(--mantine-color-${color}-6)`}
              />
            </Box>
            <Stack gap={2}>
              <Badge size="sm" variant="light" color={color}>
                {getTypeLabel()}
              </Badge>
              <Text size="xs" c="dimmed">
                {formatDateTime(waypoint.whenDone)}
              </Text>
            </Stack>
          </Group>
          <CloseButton onClick={onClose} />
        </Group>

        {/* Location Type (for waypoints) */}
        <PlaceTypeBadge value={waypoint.pointType} size="xs" />

        {/* Place Name */}
        {waypoint.whereDone && (
          <Text size="md" fw={600}>
            {waypoint.whereDone}
          </Text>
        )}

        {/* Short Note (for expenses/revenues) */}
        {waypoint.shortNote && !waypoint.whereDone && (
          <Text size="md" fw={600}>
            {waypoint.shortNote}
          </Text>
        )}

        {/* Expense Kind */}
        {waypoint.expenseKind?.name && (
          <Text size="sm" c="dimmed">
            {waypoint.expenseKind.name}
          </Text>
        )}

        {/* Address */}
        {waypoint.location && (
          <Group gap={6} wrap="nowrap">
            <AppIcon iconName="MapPin" size={16} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed" lineClamp={2}>
              {waypoint.location}
            </Text>
          </Group>
        )}

        {/* Odometer */}
        {waypoint.odometer !== undefined && waypoint.odometer !== null && (
          <Group gap={6}>
            <AppIcon iconName="Gauge" size={16} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed">
              {waypoint.odometer.toLocaleString()} {distanceLabel}
            </Text>
          </Group>
        )}

        {/* Refuel specific info */}
        {waypoint.expenseType === EXPENSE_TYPES.REFUEL && (
          <Stack gap={4}>
            {waypoint.refuelVolume && (
              <Group gap={6}>
                <AppIcon iconName="GasStation" size={16} color="var(--mantine-color-orange-5)" />
                <Text size="sm">
                  {waypoint.refuelVolume.toFixed(2)} {volumeLabel}
                  {waypoint.isFullTank && (
                    <Text span c="dimmed" size="sm">
                      {" "}
                      • {t("refuels:labels.fullTank")}
                    </Text>
                  )}
                </Text>
              </Group>
            )}
            {waypoint.fuelGrade && (
              <Group gap={6}>
                <AppIcon iconName="Droplet" size={16} color="var(--mantine-color-gray-5)" />
                <Text size="sm" c="dimmed">
                  {waypoint.fuelGrade}
                </Text>
              </Group>
            )}
          </Stack>
        )}

        {/* Amount */}
        {waypoint.totalPrice !== undefined && waypoint.totalPrice !== null && (
          <Group gap={6}>
            <AppIcon
              iconName="CurrencyDollar"
              size={16}
              color={`var(--mantine-color-${waypoint.expenseType === EXPENSE_TYPES.REVENUE ? "green" : "red"}-5)`}
            />
            <Text
              size="md"
              fw={600}
              c={waypoint.expenseType === EXPENSE_TYPES.REVENUE ? "green" : "red"}
            >
              {waypoint.expenseType === EXPENSE_TYPES.REVENUE ? "+" : "-"}
              {currencySymbol}
              {waypoint.totalPrice.toFixed(2)}
            </Text>
          </Group>
        )}

        {/* Comments */}
        {waypoint.comments && (
          <Text size="sm" c="dimmed" fs="italic">
            {waypoint.comments}
          </Text>
        )}

        {/* Edit button (for waypoints only) */}
        {isWaypoint && onEdit && (
          <Group justify="flex-end" mt="xs">
            <ActionIcon
              variant="light"
              color="blue"
              size="lg"
              onClick={() => onEdit(waypoint)}
            >
              <AppIcon iconName="Pencil" size={18} />
            </ActionIcon>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

// ============================================
// Main Component
// ============================================

const MobileMapOverlayComponent: React.FC<MobileMapOverlayProps> = ({
  opened,
  onClose,
  expenses,
  waypoints,
  distanceLabel,
  volumeLabel,
  currencySymbol,
  encodedPolyline,
  onEditWaypoint,
}) => {
  const { t } = useTranslation(["travels", "common"]);

  // State for selected waypoint (tapped on map)
  const [selectedWaypoint, setSelectedWaypoint] = useState<Expense | null>(null);

  // Handle marker click
  const handleMarkerClick = useCallback((waypoint: Expense) => {
    setSelectedWaypoint(waypoint);
  }, []);

  // Handle close detail card
  const handleCloseDetail = useCallback(() => {
    setSelectedWaypoint(null);
  }, []);

  // Handle edit from detail card
  const handleEdit = useCallback(
    (waypoint: Expense) => {
      setSelectedWaypoint(null);
      onEditWaypoint?.(waypoint);
    },
    [onEditWaypoint]
  );

  // Handle close overlay
  const handleClose = useCallback(() => {
    setSelectedWaypoint(null);
    onClose();
  }, [onClose]);

  // Don't render anything if not opened
  if (!opened) return null;

  return (
    <Overlay
      fixed
      zIndex={1000}
      backgroundOpacity={1}
      color="var(--mantine-color-body)"
    >
      <Box pos="relative" w="100%" h="100%">
        {/* Close button */}
        <Box
          pos="absolute"
          top={10}
          right={10}
          style={{ zIndex: 20 }}
        >
          <ActionIcon
            variant="filled"
            color="dark"
            size="lg"
            radius="xl"
            onClick={handleClose}
            style={{
              boxShadow: "var(--mantine-shadow-md)",
            }}
          >
            <AppIcon iconName="X" size={20} />
          </ActionIcon>
        </Box>

        {/* Title */}
        <Box
          pos="absolute"
          top={10}
          left={10}
          style={{ zIndex: 20 }}
        >
          <Paper
            px="md"
            py="xs"
            radius="md"
            shadow="sm"
          >
            <Group gap="xs">
              <AppIcon iconName="Map" size={18} />
              <Text size="sm" fw={500}>
                {t("travels:waypoints.map.title")}
              </Text>
            </Group>
          </Paper>
        </Box>

        {/* Map */}
        <WaypointsMapView
          expenses={expenses}
          hoveredWaypointId={selectedWaypoint?.id || null}
          onMarkerHover={() => { }} // No hover on mobile
          onMarkerClick={handleMarkerClick}
          height="100%"
          distanceLabel={distanceLabel}
          encodedPolyline={encodedPolyline}
        />

        {/* Selected waypoint detail card */}
        <Transition
          mounted={!!selectedWaypoint}
          transition="slide-up"
          duration={200}
        >
          {(styles) => (
            <Box
              pos="absolute"
              bottom={0}
              left={0}
              right={0}
              p="md"
              style={{
                ...styles,
                zIndex: 15,
              }}
            >
              {selectedWaypoint && (
                <WaypointDetailCard
                  waypoint={selectedWaypoint}
                  waypoints={waypoints}
                  distanceLabel={distanceLabel}
                  volumeLabel={volumeLabel}
                  currencySymbol={currencySymbol}
                  onClose={handleCloseDetail}
                  onEdit={handleEdit}
                />
              )}
            </Box>
          )}
        </Transition>

        {/* Tap hint (shown when no waypoint selected) */}
        <Transition
          mounted={!selectedWaypoint}
          transition="fade"
          duration={200}
        >
          {(styles) => (
            <Box
              pos="absolute"
              bottom={20}
              left="50%"
              style={{
                ...styles,
                transform: "translateX(-50%)",
                zIndex: 15,
              }}
            >
              <Paper
                px="md"
                py="xs"
                radius="xl"
                shadow="sm"
                style={{
                  backgroundColor: "var(--mantine-color-dark-7)",
                }}
              >
                <Text size="xs" c="white">
                  {t("travels:waypoints.map.tapHint")}
                </Text>
              </Paper>
            </Box>
          )}
        </Transition>
      </Box>
    </Overlay>
  );
};

export const MobileMapOverlay = React.memo(MobileMapOverlayComponent);